<template>
  <v-container>
    <v-toolbar
      flat
      class="mb-4"
    >
      <v-toolbar-title class="font-weight-bold blue-grey--text text--darken-4">
        {{ riskAssessment.name || riskAssessment.history?.assessment?.name }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn
        icon
        @click="$emit('close')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card
      class="mx-auto mb-6 elevation-1 rounded-lg"
      outlined
    >
      <v-card-text class="py-6">
        <v-row align="center">
          <v-col
            cols="12"
            sm="4"
            class="text-center border-right-sm"
          >
            <div
              class="text-overline grey--text text-uppercase font-weight-bold"
              style="letter-spacing: 0.08em;"
            >
              {{ $t('risk_assessment.category_label') }}
            </div>
            <div
              class="display-1 font-weight-black my-2"
              :style="{ color: getRiskColor, transition: 'color 0.3s ease' }"
            >
              {{ matchedRiskLevel ? matchedRiskLevel.name : $t('risk_assessment.calculating') }}
            </div>

            <div class="d-flex align-center justify-center mt-2 position-relative">
              <v-chip
                label
                outlined
                color="blue-grey lighten-1"
                class="font-weight-bold px-3 text-caption blue-grey--text text--darken-2"
              >
                {{ $t('risk_assessment.total_score', { score: calculatedTotalScore.toFixed(2) }) }}
              </v-chip>

              <div class="icon-overlay-wrapper ml-2">
                <v-tooltip
                  bottom
                  max-width="320"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      color="blue-grey lighten-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <div class="text-caption pa-1">
                    <div class="font-weight-bold mb-1">
                      {{ $t('risk_assessment.tooltip.title') }}
                    </div>
                    <div class="grey--text text--lighten-2">
                      {{ $t('risk_assessment.tooltip.formula') }}
                    </div>
                    <div
                      class="mt-1 grey--text text--lighten-1"
                      style="font-size: 0.7rem !important;"
                    >
                      {{ $t('risk_assessment.tooltip.description') }}
                    </div>
                  </div>
                </v-tooltip>
              </div>
            </div>
          </v-col>

          <v-col
            cols="12"
            sm="8"
            class="px-6"
          >
            <div class="d-flex justify-space-between text-caption font-weight-bold mb-2 grey--text text--darken-1">
              <span>{{ $t('risk_assessment.min_label', { score: calculatedMinScore.toFixed(1) }) }}</span>
              <span>{{ $t('risk_assessment.scale_spectrum') }}</span>
              <span>{{ $t('risk_assessment.max_label', { score: calculatedMaxScore.toFixed(1) }) }}</span>
            </div>

            <div class="risk-meter-container my-4">
              <div class="risk-meter-track" />
              <div
                class="risk-meter-indicator"
                :style="{
                  left: `${boundedPercentage}%`,
                  backgroundColor: getRiskColor
                }"
              >
                <div
                  class="indicator-label elevation-2"
                  :style="{ backgroundColor: getRiskColor }"
                >
                  {{ calculatedTotalScore.toFixed(1) }}
                </div>
              </div>
            </div>

            <div class="text-right text-caption grey--text text--darken-1 mt-5 font-italic">
              <template v-if="riskLevelRanges.length">
                {{ $t('risk_assessment.scale_legend') }}
                <span
                  v-for="(range, idx) in riskLevelRanges"
                  :key="idx"
                >
                  <strong>{{ range.name }}</strong> (≥ {{ range.minScore }})<slot v-if="idx < riskLevelRanges.length - 1"> • </slot>
                </span>
              </template>
              <template v-else>
                {{ $t('risk_assessment.default_fallback_text') }}
              </template>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div class="d-flex justify-end mb-4">
      <v-switch
        v-model="showWeightImpact"
        :label="$t('risk_assessment.show_weight_impact')"
        hide-details
        color="slate-grey"
        class="mt-0 font-weight-medium text-body-2"
      />
    </div>

    <template v-for="activeFactor in activeFactorsList">
      <v-card
        v-if="hasHistoricalData(activeFactor.id)"
        :key="activeFactor.id"
        class="mt-6 mb-6 elevation-1 rounded-lg"
        outlined
      >
        <v-card-title class="neutral-bg subtitle-1 font-weight-bold d-flex align-center py-3 border-bottom">
          <span class="blue-grey--text text--darken-4">
            {{ activeFactor.name }}
          </span>
          <v-spacer />
          <v-chip
            color="blue-grey text--darken-1"
            outlined
            small
            class="font-weight-bold"
          >
            {{ $t('risk_assessment.section_weight', { ratio: Number(activeFactor.ratio || 0).toFixed(0) }) }}
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr class="neutral-bg">
                  <th
                    :width="showWeightImpact ? '45%' : '55%'"
                    class="text-uppercase text-caption font-weight-bold blue-grey--text text--darken-2"
                  >
                    {{ $t('risk_assessment.table.question_parameters') }}
                  </th>
                  <th
                    :width="showWeightImpact ? '35%' : '45%'"
                    class="text-uppercase text-caption font-weight-bold blue-grey--text text--darken-2"
                  >
                    {{ $t('risk_assessment.table.collected_response_data') }}
                  </th>
                  <th
                    v-if="showWeightImpact"
                    width="20%"
                    class="text-right text-uppercase text-caption font-weight-bold blue-grey--text text--darken-2"
                  >
                    {{ $t('risk_assessment.table.weight_impact') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="question in activeFactor.questions">
                  <tr
                    v-if="getQuestionHistoryItem(activeFactor.id, question.id)"
                    :key="question.id"
                    class="table-row-hover"
                  >
                    <td class="text-body-2 font-weight-medium py-4 text-wrap blue-grey--text text--darken-4">
                      {{ question.name }}
                    </td>
                    <td class="text-body-2 grey--text text--darken-3 py-4">
                      <div class="font-weight-bold text-wrap mb-1">
                        {{ getAnswerText(question, getQuestionHistoryItem(activeFactor.id, question.id).answer) }}
                      </div>
                      <div class="text-caption grey--text text--darken-1">
                        {{ $t('risk_assessment.table.assigned_score') }} <strong class="blue-grey--text text--darken-3">{{ getQuestionHistoryItem(activeFactor.id, question.id).score.toFixed(1) }}</strong> / {{ getQuestionHistoryItem(activeFactor.id, question.id).max.toFixed(1) }}
                      </div>
                    </td>
                    <td
                      v-if="showWeightImpact"
                      class="text-right py-4"
                    >
                      <div class="d-flex align-center justify-end">
                        <div class="text-right mr-3">
                          <div class="text-body-2 font-weight-bold blue-grey--text text--darken-3 lh-1">
                            {{ Number(question.ratio || 0).toFixed(0) }}%
                          </div>
                          <div class="text-caption grey--text font-weight-light mt-0">
                            {{ $t('risk_assessment.of_total') }}
                          </div>
                        </div>

                        <div
                          class="mini-pie-indicator"
                          :style="{ '--percentage': `${Number(question.ratio || 0)}%` }"
                          :title="`${Number(question.ratio || 0)}% ${$t('risk_assessment.of_total')}`"
                        />
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script>
export default {
  props: {
    riskAssessment: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      showWeightImpact: false
    }
  },
  computed: {
    activeFactorsList () {
      return this.riskAssessment?.history?.assessment?.factors || []
    },
    calculatedTotalScore () {
      return this.getActiveFactorValues('score')
    },
    calculatedMaxScore () {
      return this.getActiveFactorValues('max')
    },
    calculatedMinScore () {
      return this.getActiveFactorValues('min')
    },
    boundedPercentage () {
      const min = this.calculatedMinScore
      const max = this.calculatedMaxScore === min ? min + 100 : this.calculatedMaxScore
      const percentage = ((this.calculatedTotalScore - min) / (max - min)) * 100
      return Math.min(Math.max(percentage, 0), 100)
    },
    riskLevelRanges () {
      const levels = this.riskAssessment?.history?.assessment?.levels
      if (!levels || !Array.isArray(levels) || levels.length === 0) return []
      return [...levels]
        .sort((a, b) => a.score - b.score)
        .map(level => ({
          name: level.name,
          minScore: Number(level.score || 0).toFixed(1)
        }))
    },
    matchedRiskLevel () {
      if (this.riskAssessment?.history?.level) {
        return this.riskAssessment.history.level
      }

      const levels = this.riskAssessment?.history?.assessment?.levels
      if (!levels || !Array.isArray(levels) || levels.length === 0) {
        return this.riskAssessment?.level ? { name: this.riskAssessment.level } : null
      }

      const sortedLevels = [...levels].sort((a, b) => b.score - a.score)
      const currentScore = this.calculatedTotalScore

      const matched = sortedLevels.find(level => currentScore >= level.score)
      return matched || sortedLevels[sortedLevels.length - 1]
    },
    getRiskColor () {
      if (!this.matchedRiskLevel) return '#64748B'

      const levels = this.riskAssessment?.history?.assessment?.levels
      if (!levels || !Array.isArray(levels) || levels.length === 0) {
        const name = String(this.matchedRiskLevel.name).toLowerCase()
        if (name.includes('low') || name.includes('safe')) return '#15803D'
        if (name.includes('high') || name.includes('crit')) return '#B91C1C'
        return '#B45309'
      }

      const sortedLevels = [...levels].sort((a, b) => a.score - b.score)
      const totalTiers = sortedLevels.length
      const currentIndex = sortedLevels.findIndex(l => l.name === this.matchedRiskLevel.name)

      if (currentIndex === 0) return '#15803D'
      if (currentIndex === totalTiers - 1) return '#B91C1C'

      const ratio = currentIndex / (totalTiers - 1)
      if (ratio < 0.35) return '#16A34A'
      if (ratio < 0.6) return '#D97706'
      if (ratio < 0.8) return '#C2410C'
      return '#991B1B'
    }
  },
  methods: {
    getActiveFactorValues (propertyKey) {
      const factorScores = this.riskAssessment?.history?.factor_scores
      if (!factorScores) return 0
      return Object.values(factorScores).reduce((accumulator, item) => {
        if (item && item[propertyKey] !== undefined) {
          return accumulator + (item[propertyKey] || 0)
        }
        return accumulator
      }, 0)
    },
    hasHistoricalData (factorId) {
      return !!(this.riskAssessment?.history?.factor_scores?.[factorId])
    },
    getFactorHistoryItem (factorId) {
      return this.riskAssessment?.history?.factor_scores?.[factorId] || { score: 0, max: 0 }
    },
    getQuestionHistoryItem (factorId, questionId) {
      return this.riskAssessment?.history?.factor_scores?.[factorId]?.question_scores?.[questionId] || null
    },
    getAnswerText (questionObj, answeredId) {
      if (!questionObj || !questionObj.options) return ''
      const matchedAnswer = questionObj.options.find(opt => opt.id === answeredId)
      return matchedAnswer ? matchedAnswer.name : ''
    }
  }
}
</script>

<style scoped>
/* 1. Horizontal Scale Track Meter */
.risk-meter-container {
  position: relative;
  height: 8px;
  margin-top: 30px !important;
}
.risk-meter-track {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(to right, #16A34A, #EAB308, #D97706, #B91C1C);
  opacity: 0.9;
}
.risk-meter-indicator {
  position: absolute;
  top: 50%;
  width: 4px;
  height: 18px;
  transform: translate(-50%, -50%);
  border-radius: 2px;
  transition: left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.indicator-label {
  position: absolute;
  top: -28px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.15);
}

/* 2. System Palette Mini-Donut */
.mini-pie-indicator {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: conic-gradient(#475569 0% var(--percentage), #E2E8F0 var(--percentage) 100%);
  position: relative;
  display: inline-block;
}
.mini-pie-indicator::after {
  content: '';
  position: absolute;
  top: 5px;
  left: 5px;
  width: 10px;
  height: 10px;
  background: #ffffff;
  border-radius: 50%;
}

/* 3. Essential Neutral Cleanups */
.neutral-bg {
  background-color: #F8FAFC !important;
}
.border-bottom {
  border-bottom: 1px solid #E2E8F0 !important;
}
.text-wrap {
  white-space: normal !important;
  word-break: break-word;
}
.table-row-hover:hover {
  background-color: #F1F5F9;
}
.lh-1 {
  line-height: 1.1 !important;
}
@media (min-width: 600px) {
  .border-right-sm {
    border-right: 1px solid #E2E8F0;
  }
}
</style>
